import { httpClient } from './api';
import { grundHeader } from '../components/ui/loeschen-mit-grund-dialog';

export type TrialStatus =
  | 'scheduled'
  | 'confirmed'
  | 'started'
  | 'completed'
  | 'no_show'
  | 'cancelled'
  | 'rescheduled'
  | 'offer_sent';

/**
 * Gehört die Probestunde in einen Terminplan (Stundenplan, Kalender)? Abgesagte nicht —
 * der Platz ist frei. In der Probestunden-Liste bleiben sie sichtbar. Gleiche Regel wie
 * das Lehrerportal und die KI-Tools (backend: lehrerportal.ABGESAGTE_PROBESTUNDEN).
 */
export const stehtImTerminplan = (trial: Pick<TrialLessonDTO, 'status'>): boolean =>
  trial.status !== 'cancelled';

export interface TrialLessonDTO {
  id: string;
  studentName: string;
  contactPhone?: string;
  contactEmail?: string;
  teacherId?: string;
  teacherName?: string;
  instrument?: string;
  fachName?: string;
  fachId?: number;
  date: string; // YYYY-MM-DD
  startTime: string; // HH:MM:SS
  endTime: string; // HH:MM:SS
  status: TrialStatus;
  notes?: string | null;
  roomId?: string | null;
  roomName?: string | null;
  subprofileId?: string | null;
  courseId?: number | null;
  enrollment?: { id: number; status: string } | null;
  order?: { id: number; status: string } | null;
  latestHistoryDescription?: string | null;
  latestHistoryAt?: string | null;
  isFavorite?: boolean;
  process?: {
    state: string | null;
    considerUntil: string | null; // YYYY-MM-DD
    reasonKey: string | null;
    reasonText: string | null;
  };
}

export interface TrialHistoryItem {
  id: number;
  trialId: number;
  type: string;
  description: string;
  details?: string | null;
  meta?: unknown | null;
  actorUserId?: number | null;
  actorName?: string | null;
  actorEmail?: string | null;
  createdAt: string;
}

export interface CreateTrialHistoryPayload {
  type: string;
  description: string;
  details?: string | null;
  meta?: unknown | null;
  historyTemplateKey?: string;
  historyTemplateVars?: Record<string, unknown>;
}

export interface CreateTrialPayload {
  studentName?: string;
  firstName?: string;
  lastName?: string;
  contactPhone?: string;
  contactEmail?: string;
  teacherId?: string | number | null;
  fachId?: number;
  date: string; // YYYY-MM-DD
  startTime: string; // HH:MM
  endTime: string; // HH:MM
  roomId?: string | number | null;
  notes?: string;
  subprofileId: string | number;
  sendEmails?: boolean;
  skipStudentEmail?: boolean;
  skipTeacherEmail?: boolean;
  sendSms?: boolean;
  skipStudentSms?: boolean;
  skipTeacherSms?: boolean;
  courseId?: number;
  standort_id?: number;
  historyTemplateKey?: string;
  historyTemplateType?: string;
  /** Der im Assistenten angezeigte Text — er wird verschickt, nicht die Vorlage. */
  drafts?: Record<string, { subject: string; html: string; text: string; sms: string }>;
  history?: { type?: string; description: string; details?: string | null };
}

export const fetchTrials = async (start?: string, end?: string, standortId?: number): Promise<TrialLessonDTO[]> => {
  const params: Record<string, string> = {};
  if (start) params.start = start;
  if (end) params.end = end;
  if (standortId !== undefined) params.standort_id = String(standortId);
  const res = await httpClient<TrialLessonDTO[]>(`/api/trials${Object.keys(params).length ? `?${new URLSearchParams(params)}` : ''}`);
  return res.data || [];
};

/**
 * Eine einzelne Probestunde.
 *
 * Zwei Assistenten holten dafür die ganze Liste des Mandanten und suchten darin —
 * standortblind und mit einer Abfrage je Probestunde im Rücken.
 */
export const fetchTrial = async (trialId: string | number): Promise<TrialLessonDTO | null> => {
  const res = await httpClient<TrialLessonDTO>(`/api/trials/${encodeURIComponent(String(trialId))}`);
  return res.data ?? null;
};

export type TrialFavoritEintrag = { accountId: string | null; name: string };

/** Meine Favoriten und je Probestunde, wer sie vorgemerkt hat (T-0014). */
export const listTrialFavorites = async (
  standortId: number,
): Promise<{ meine: number[]; favoriten: Record<number, TrialFavoritEintrag[]> }> => {
  const res = await httpClient<{ trial_ids: number[]; favoriten?: Record<string, TrialFavoritEintrag[]> }>(
    `/api/trials/favorites?standort_id=${encodeURIComponent(String(standortId))}`,
  );
  if (res.error || !res.data) throw new Error(res.error || 'Favoriten konnten nicht geladen werden');
  const favoriten: Record<number, TrialFavoritEintrag[]> = {};
  Object.entries(res.data.favoriten || {}).forEach(([id, eintraege]) => {
    favoriten[Number(id)] = Array.isArray(eintraege) ? eintraege : [];
  });
  return { meine: res.data.trial_ids || [], favoriten };
};

type FavoritAntwort = { ist_favorit: boolean; favoriten: TrialFavoritEintrag[] };

export const setTrialFavorite = async (id: string | number): Promise<FavoritAntwort> => {
  const res = await httpClient<FavoritAntwort>(`/api/trials/${id}/favorite`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({}),
  });
  if (res.error || !res.data) throw new Error(res.error || 'Favorit konnte nicht gesetzt werden');
  return res.data;
};

export const removeTrialFavorite = async (id: string | number): Promise<FavoritAntwort> => {
  const res = await httpClient<FavoritAntwort>(`/api/trials/${id}/favorite`, {
    method: 'DELETE',
  });
  if (res.error || !res.data) throw new Error(res.error || 'Favorit konnte nicht entfernt werden');
  return res.data;
};

export const createTrial = async (payload: CreateTrialPayload): Promise<TrialLessonDTO> => {
  const res = await httpClient<TrialLessonDTO>('/api/trials', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload)
  });
  if (!res.data) throw new Error(res.error || 'Fehler beim Anlegen der Probestunde');
  return res.data;
};

/** Entwurf einer Nachricht, wie ihn der Assistent bearbeitet hat. */
export type MessageDraft = { subject?: string; html?: string; text?: string; sms?: string };

/** Was beim Versand schiefging — leer heißt: alles raus. */
export type TrialCommunicationResult = { emailError?: string | null; smsError?: string | null };

export const rescheduleTrial = async (
  id: string | number,
  data: {
    date: string;
    startTime: string;
    endTime: string;
    roomId?: number | string | null;
    teacherId?: number | string | null;
    notes?: string;
    historyTemplateKey?: string;
    channels?: { email?: boolean; sms?: boolean };
    notify?: {
      student?: { email?: boolean; sms?: boolean };
      teacher?: { email?: boolean; sms?: boolean };
    };
    email?: Partial<{
      subjectStudent: string;
      htmlStudent: string;
      textStudent?: string;
      subjectTeacher?: string;
      htmlTeacher?: string;
      textTeacher?: string;
      toStudent?: string;
      toTeacher?: string;
    }>;
    history?: Partial<{ wpUserId: number; subIndex?: number; description: string; details?: string; type?: string }>;
    sms?: Partial<{ textStudent: string; textTeacher: string }>;
    /** Bearbeiteter Text je Empfänger; das Backend verschickt ihn statt der Vorlage. */
    drafts?: Partial<Record<'student' | 'teacher', MessageDraft>>;
  }
): Promise<TrialLessonDTO & { communication?: TrialCommunicationResult }> => {
  const res = await httpClient<TrialLessonDTO & { communication?: TrialCommunicationResult }>(`/api/trials/${id}/reschedule`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data)
  });
  if (!res.data) throw new Error(res.error || 'Fehler beim Verschieben');
  return res.data;
};

export const cancelTrial = async (id: string | number, reason?: string): Promise<TrialLessonDTO> => {
  const res = await httpClient<TrialLessonDTO>(`/api/trials/${id}/cancel`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ reason })
  });
  if (!res.data) throw new Error(res.error || 'Fehler beim Absagen');
  return res.data;
};

export const cancelTrialAdvanced = async (
  id: string | number,
  payload: {
    reasonKey: 'student_cancelled' | 'teacher_cancelled' | 'no_show' | 'other';
    reasonText?: string;
    historyTemplateKey?: string;
    email?: Partial<{
      send: boolean;
      subjectTeacher: string;
      htmlTeacher: string;
      textTeacher?: string;
      toTeacher?: string;
      subjectStudent: string;
      htmlStudent: string;
      textStudent?: string;
      toStudent?: string;
    }>;
    history?: Partial<{ wpUserId: number; subIndex?: number; description: string; details?: string; type?: string }>;
    task?: Partial<{ title?: string; description?: string; dueAt: string; assignedUserId: number; personHistoryId?: number }>;
    drafts?: Partial<Record<'student' | 'teacher', { subject?: string; html?: string; text?: string; sms?: string }>>;
    channels?: { email: boolean; sms: boolean };
    notify?: Partial<Record<'student' | 'teacher', { email: boolean; sms: boolean }>>;
  }
): Promise<TrialLessonDTO & { communication?: TrialCommunicationResult }> => {
  const res = await httpClient<TrialLessonDTO & { communication?: TrialCommunicationResult }>(`/api/trials/${id}/cancel`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload)
  });
  if (!res.data) throw new Error(res.error || 'Fehler beim Absage-Workflow');
  return res.data;
};

export const setTrialStatus = async (
  id: string | number,
  status: TrialStatus
): Promise<TrialLessonDTO> => {
  const res = await httpClient<TrialLessonDTO>(`/api/trials/${id}/set-status`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ status }),
  });
  if (!res.data) throw new Error(res.error || 'Fehler beim Setzen des Status');
  return res.data;
};

export const sendTrialOffer = async (
  id: string | number,
  payload: {
    email?: Partial<{
      send: boolean;
      toTeacher: string;
      templateKeyTeacher: string;
      subjectTeacher: string;
      htmlTeacher: string;
      textTeacher?: string;
    }>;
    historyTemplateKey?: string;
    history?: Partial<{
      type: string;
      description: string;
      details?: string;
    }>;
    task?: Partial<{
      title?: string;
      description?: string;
      dueAt: string;
      assignedUserId: number;
      personHistoryId?: number;
    }>;
  } = {}
): Promise<{
  ok: boolean;
  resend?: boolean;
  emailOk?: boolean | null;
  historyOk?: boolean | null;
  taskOk?: boolean | null;
  trial?: TrialLessonDTO;
  error?: string;
}> => {
  const res = await httpClient<{
    ok: boolean;
    resend?: boolean;
    emailOk?: boolean | null;
    historyOk?: boolean | null;
    taskOk?: boolean | null;
    trial?: TrialLessonDTO;
    error?: string;
  }>(`/api/trials/${id}/send-offer`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.data) throw new Error(res.error || 'Fehler beim Senden des Angebots');
  return res.data;
};

export const resendTrialConfirmation = async (
  id: string | number,
  payload: { standortId?: number; channels?: { email?: boolean; sms?: boolean }; drafts?: { student?: MessageDraft }; reason?: string } = {}
): Promise<{ trial: TrialLessonDTO; email?: unknown; sms?: unknown }> => {
  const res = await httpClient<{ trial: TrialLessonDTO; email?: unknown; sms?: unknown }>(`/api/trials/${id}/resend-confirmation`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.data) throw new Error(res.error || 'Bestätigung konnte nicht erneut versendet werden');
  return res.data;
};

export const setTrialConsiderUntil = async (
  id: string | number,
  payload: { considerUntil: string; standortId?: number; channels?: { email?: boolean; sms?: boolean }; notify?: { student?: boolean; teacher?: boolean }; drafts?: { student?: MessageDraft }; reason?: string }
): Promise<{ trial: TrialLessonDTO; email?: unknown; sms?: unknown }> => {
  const res = await httpClient<{ trial: TrialLessonDTO; email?: unknown; sms?: unknown }>(`/api/trials/${id}/set-consider-until`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.data) throw new Error(res.error || 'Status konnte nicht gesetzt werden');
  return res.data;
};

/** Je Empfänger: welche Kanäle (T-0307). */
type KanalWahl = { email?: boolean; sms?: boolean };

export const markTrialNotStarted = async (
  id: string | number,
  payload: {
    reasonKey: string;
    reasonText?: string;
    standortId?: number;
    channels?: { email?: boolean; sms?: boolean };
    notify?: { student?: boolean | KanalWahl; teacher?: boolean | KanalWahl };
    drafts?: { student?: MessageDraft; teacher?: MessageDraft };
    reason?: string;
  }
): Promise<{ trial: TrialLessonDTO; email?: unknown; sms?: unknown; teacher?: { emailError?: string | null; smsError?: string | null } }> => {
  const res = await httpClient<{ trial: TrialLessonDTO; email?: unknown; sms?: unknown; teacher?: { emailError?: string | null; smsError?: string | null } }>(`/api/trials/${id}/mark-not-started`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.data) throw new Error(res.error || 'Status konnte nicht gesetzt werden');
  return res.data;
};

export const markTrialNoOccurrence = async (
  id: string | number,
  payload: { reasonKey: string; reasonText?: string; standortId?: number; channels?: { email?: boolean; sms?: boolean }; notify?: { student?: boolean; teacher?: boolean }; drafts?: { student?: MessageDraft }; reason?: string }
): Promise<{ trial: TrialLessonDTO; email?: unknown; sms?: unknown }> => {
  const res = await httpClient<{ trial: TrialLessonDTO; email?: unknown; sms?: unknown }>(`/api/trials/${id}/mark-no-occurrence`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.data) throw new Error(res.error || 'Status konnte nicht gesetzt werden');
  return res.data;
};

export const trialHistoryApi = {
  list: async (id: string | number): Promise<TrialHistoryItem[]> => {
    const res = await httpClient<TrialHistoryItem[]>(`/api/trials/${id}/history`);
    if (res.error || !res.data) throw new Error(res.error || 'Historie konnte nicht geladen werden');
    return res.data;
  },
  create: async (id: string | number, payload: CreateTrialHistoryPayload): Promise<TrialHistoryItem> => {
    const res = await httpClient<TrialHistoryItem>(`/api/trials/${id}/history`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (res.error || !res.data) throw new Error(res.error || 'Historien-Eintrag fehlgeschlagen');
    return res.data;
  },
  delete: async (id: string | number, entryId: number, grund: string): Promise<void> => {
    const res = await httpClient<unknown>(`/api/trials/${id}/history/${entryId}`, {
      method: 'DELETE',
      ...grundHeader(grund),
    });
    if (res.error) throw new Error(res.error || 'Historien-Eintrag konnte nicht gelöscht werden');
  },
};


